import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { isSuperAdmin } from '../../utils/crmPermissions';
import { Plus, Edit, Archive, UserCog } from 'lucide-react';

export default function CrmDepartments() {
  const { toast } = useToast();
  const { user } = useAuth();
  const canAdmin = isSuperAdmin(user);
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [hodOpen, setHodOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ department_name: '', department_code: '', description: '' });
  const [hodDeptId, setHodDeptId] = useState(null);
  const [hodEmployeeId, setHodEmployeeId] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/crm/departments');
      setItems(res.data?.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to load departments', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ department_name: '', department_code: '', description: '' });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      department_name: row.department_name || '',
      department_code: row.department_code || '',
      description: row.description || '',
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/crm/departments/${editing.id}`, form);
        toast({ title: 'Department updated' });
      } else {
        await api.post('/crm/departments', form);
        toast({ title: 'Department created' });
      }
      setOpen(false);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Save failed', variant: 'destructive' });
    }
  };

  const archive = async (row) => {
    try {
      await api.post(`/crm/departments/${row.id}/archive`);
      toast({ title: 'Department archived' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Archive failed', variant: 'destructive' });
    }
  };

  const openHod = async (row) => {
    setHodDeptId(row.id);
    setHodEmployeeId(row.head_employee_id ? String(row.head_employee_id) : '');
    try {
      const res = await api.get('/crm/employees');
      setEmployees(res.data?.data?.items || []);
    } catch {
      setEmployees([]);
    }
    setHodOpen(true);
  };

  const saveHod = async () => {
    try {
      if (hodEmployeeId) {
        await api.post(`/crm/departments/${hodDeptId}/assign-hod`, { employee_id: Number(hodEmployeeId) });
        toast({ title: 'HOD assigned' });
      } else {
        await api.post(`/crm/departments/${hodDeptId}/remove-hod`);
        toast({ title: 'HOD removed' });
      }
      setHodOpen(false);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'HOD update failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-xl font-semibold text-foreground">Departments</h1>
        {canAdmin && (
          <Button onClick={openCreate} size="sm"><Plus className="w-4 h-4 mr-1" /> Add Department</Button>
        )}
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">All Departments</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!loading && items.length === 0 && <p className="text-sm text-muted-foreground">No departments yet.</p>}
          {items.map((row) => (
            <div key={row.id} className="flex items-center justify-between gap-2 border-b border-border py-3 last:border-0">
              <div>
                <div className="font-medium text-foreground">{row.department_name}</div>
                <div className="text-xs text-muted-foreground">
                  {row.department_code || '—'} · {row.status}
                  {row.head_first_name ? ` · HOD: ${row.head_first_name} ${row.head_last_name || ''}` : ' · No HOD'}
                </div>
                <Link to={`/crm/roles?department_id=${row.id}`} className="text-xs text-primary underline">View roles</Link>
              </div>
              {canAdmin && (
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(row)} title="Edit"><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => openHod(row)} title="Assign HOD"><UserCog className="w-4 h-4" /></Button>
                  {row.status !== 'archived' && (
                    <Button variant="ghost" size="icon" onClick={() => archive(row)} title="Archive"><Archive className="w-4 h-4" /></Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Department' : 'New Department'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.department_name} onChange={(e) => setForm({ ...form, department_name: e.target.value })} /></div>
            <div><Label>Code</Label><Input value={form.department_code} onChange={(e) => setForm({ ...form, department_code: e.target.value })} /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={hodOpen} onOpenChange={setHodOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign HOD</DialogTitle></DialogHeader>
          <div>
            <Label>Employee</Label>
            <Select value={hodEmployeeId || 'none'} onValueChange={(v) => setHodEmployeeId(v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No HOD</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={String(e.id)}>
                    {e.first_name} {e.last_name} ({e.employee_code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHodOpen(false)}>Cancel</Button>
            <Button onClick={saveHod}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
